import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react";

import { contextConstants } from "context/constants";
import { contextReducers } from "context/reducers";
import { contextActions } from "context/actions";
import { JobsContext } from "./JobsProvider";
import { AuthContext } from "./AuthProvider";
import { AlertContext } from "./AlertProvider";
import { convertMilesToMeters } from "utils/common";
import { isCoveredJobMatchStatusError } from "utils/helpers/match";
import {
  IDEAL_FIT_SEARCH_TYPES,
  JOB_APPLICATION_MATCH_STATUS_FORMATTED,
  SEARCH_SOURCE,
} from "lookup";

const STATUSES_KEYS_LINKING = {
  SHORTLISTED: "isShortlisting",
  MATCHED: "isMatching",
  DELETED: "isDeleting",
  SKIPPED: "isSkipping",
};

const _getMatchStatusLabel = (status) => {
  return JOB_APPLICATION_MATCH_STATUS_FORMATTED[status] || status;
};

export const MatchContext = createContext();

function ErrorLoadingIdeal() {
  return (
    <div>
      <p>An error occurred fetching the ideal candidates for the job. </p>
      <p>Try refreshing the page.</p>
      <p>If error persists, contact Torc Support.</p>
    </div>
  );
}

export const MatchProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { jobOpp, updateJobLocally } = useContext(JobsContext);
  const { addCustomAlert } = useContext(AlertContext);

  const [state, dispatch] = useReducer(
    contextReducers.match.reducer,
    contextReducers.match.initialState
  );

  const updateMappedTabHitsMatchesForDownload = useCallback((list) => {
    dispatch({
      type: contextConstants.match.MAPPED_HITS_TAB_MATCHES_FOR_DOWNLOAD_UPDATED,
      payload: { mappedHitsTabMatchesForDownload: list },
    });
  }, []);

  const initMatches = () => {
    const matches = contextActions.match.initMatches(jobOpp);

    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches },
    });
  };

  const initIdeal = async () => {
    if (user.canFetchIdealCandidates) {
      let geoDistance = null;

      if (jobOpp.location?.locationId) {
        // Default geo distance is 50 miles
        geoDistance = convertMilesToMeters(50);
      }
      // Fetch ideal candidates without overriding the required and optional skills (by default)
      await fetchIdealCandidates({
        requiredSkills: null,
        optionalSkills: null,
        geoDistance,
        searchType: IDEAL_FIT_SEARCH_TYPES.ADVANCED,
      });
    }
  };

  const fetchIdealCandidates = async ({
    requiredSkills,
    optionalSkills,
    geoDistance,
    basedOnWorkHistory,
    searchType,
  }) => {
    let ideal = [];
    let loadingIdeal = "";

    dispatch({
      type: contextConstants.match.LOADING_IDEAL_CANDIDATES,
    });

    try {
      ideal = await contextActions.match.initIdealCandidates({
        jobOpp,
        requiredSkills,
        optionalSkills,
        geoDistance,
        basedOnWorkHistory,
        searchType,
        mainRecordsSource: SEARCH_SOURCE.OPEN_SEARCH,
      });
    } catch (err) {
      loadingIdeal = <ErrorLoadingIdeal />;
    }

    dispatch({
      type: contextConstants.match.IDEAL_CANDIDATES_LOADED,
      payload: { ideal, loadingIdeal },
    });
  };

  const updateMatchFieldLocally = (applicationId, fieldKey, value) => {
    const matches = contextActions.match.updateMatchFieldLocally(
      applicationId,
      fieldKey,
      value,
      state.matches
    );

    const updatedMatch = matches.find(
      (el) => el.applicationId === applicationId
    );

    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches },
    });

    return updatedMatch;
  };

  const updateMatch1LevelFieldLocally = (applicationId, fieldKey, value) => {
    dispatch({
      type: contextConstants.match.MATCH_1_LEVEL_FIELD_UPDATED,
      payload: { applicationId, fieldKey, value },
    });
  };

  const updateMatchUserFieldLocally = (applicationId, fieldKey, value) => {
    dispatch({
      type: contextConstants.match.MATCH_USER_FIELD_UPDATED,
      payload: { applicationId, fieldKey, value },
    });
  };

  const updateIdealCandidateFieldLocally = (userId, fieldKey, value) => {
    dispatch({
      type: contextConstants.match.IDEAL_CANDIDATE_FIELD_UPDATED,
      payload: { userId, fieldKey, value },
    });
  };

  const removeMatchLocally = (applicationId) => {
    const matches = contextActions.match.removeMatchLocally(
      applicationId,
      state.matches
    );

    // Any change done to the job, match sync is triggered by the useEffect calling
    // initMatches which dispatch job matches into state matches provider.
    const jobOppMatches = { ...jobOpp?.matches, items: matches };

    updateJobLocally(jobOppMatches, "matches");
  };

  const replaceMatchLocally = (match) => {
    const matches = contextActions.match.replaceMatchLocally(
      match,
      state.matches
    );

    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches },
    });

    const jobOppMatches = { ...jobOpp?.matches, items: matches };

    updateJobLocally(jobOppMatches, "matches");
  };

  const createMatch = async (jobOpportunityId, userId, args) => {
    try {
      const newMatch = await contextActions.match.createMatch({
        jobOpportunityId,
        userId,
        ...args,
      });

      const jobOppMatches = {
        ...jobOpp?.matches,
        items: [...(jobOpp?.matches?.items || []), newMatch.data.createMatch],
      };

      // Any change done to the job, match sync is triggered by the useEffect calling
      // initMatches which dispatch job matches into state matches provider.
      updateJobLocally(jobOppMatches, "matches");
    } catch (error) {
      console.log("callMatchAction error: ", error);

      if (isCoveredJobMatchStatusError(error)) {
        const statusLabel = _getMatchStatusLabel(args.status);

        addCustomAlert({
          title: "Job is covered",
          duration: 5000,
          variation: "warning",
          message: `This candidate cannot be moved to ${statusLabel} because the job is in Covered (hold) status.`,
        });
      }
    }
  };

  const updateMatch = async (applicationId, jobOpportunityId, args) => {
    try {
      const existingMatch = state.matches.find(
        (el) => el.applicationId === applicationId
      );

      await contextActions.match.updateMatch({
        applicationId,
        jobOpportunityId,
        ...args,
      });

      const params = {
        ...existingMatch,
        ...args,
      };
      if (args.status) {
        params[STATUSES_KEYS_LINKING[args.status]] = false;
      }

      replaceMatchLocally(params);
    } catch (error) {
      console.log("callMatchAction error: ", error);

      if (isCoveredJobMatchStatusError(error)) {
        const statusLabel = _getMatchStatusLabel(args.status);

        addCustomAlert({
          title: "Job is covered",
          duration: 3000,
          variation: "warning",
          message: `This candidate cannot be moved to ${statusLabel} because the job is in Covered (hold) status.`,
        });
      }
    }
  };

  const removeMatch = async (applicationId, jobOpportunityId) => {
    try {
      await contextActions.match.removeMatch(applicationId, jobOpportunityId);

      removeMatchLocally(applicationId);
    } catch (error) {
      console.log("removeMatch error: ", error);
    }
  };

  const clearMatches = () => {
    dispatch({
      type: contextConstants.match.MATCHES_LOADED,
      payload: { matches: [] },
    });

    dispatch({
      type: contextConstants.match.IDEAL_CANDIDATES_LOADED,
      payload: { ideal: [] },
    });
  };

  const handleIsReRankingMatches = (isReRankingMatches) => {
    dispatch({
      type: contextConstants.match.IS_RE_RANKING_MATCHES_CHANGED,
      payload: { isReRankingMatches },
    });
  };

  const match = {
    ...state,
    initMatches,
    createMatch,
    updateMatch,
    removeMatch,
    updateMatchFieldLocally,
    updateMatch1LevelFieldLocally,
    updateMatchUserFieldLocally,
    updateIdealCandidateFieldLocally,
    removeMatchLocally,
    replaceMatchLocally,
    clearMatches,
    updateMappedTabHitsMatchesForDownload,
    fetchIdealCandidates,
    handleIsReRankingMatches,
  };

  useEffect(() => {
    if (jobOpp?.id) {
      initMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOpp]);

  useEffect(() => {
    if (jobOpp?.id) {
      initIdeal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOpp]);

  return (
    <MatchContext.Provider value={match}>{children}</MatchContext.Provider>
  );
};
